import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import MediaPickerModal from '../components/MediaPickerModal';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Store, ExternalLink, Plus, Pencil, Trash2, Star, MousePointerClick, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

const emptyForm = { name: '', bio: '', logo_url: '', link: '', featured: false, order: 0 };

const normalizeHref = (url) => {
  if (!url) return '#';
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  return `https://${url}`;
};

const Marketplace = () => {
  const { user, isImpersonating } = useAuth();
  const isMasterAdmin = !isImpersonating && user?.role === 'master_admin';

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const fetchPartners = useCallback(async () => {
    try {
      const res = await api.get('/marketplace/partners');
      setPartners(res.data || []);
    } catch (e) {
      toast.error('Failed to load marketplace partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const handleVisit = async (partner) => {
    window.open(normalizeHref(partner.link), '_blank', 'noopener,noreferrer');
    try {
      await api.post(`/marketplace/partners/${partner.partner_id}/click`);
      setPartners((prev) => prev.map((p) =>
        p.partner_id === partner.partner_id ? { ...p, click_count: (p.click_count || 0) + 1 } : p));
    } catch (e) { /* non-blocking */ }
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.partner_id);
    setForm({ name: p.name, bio: p.bio || '', logo_url: p.logo_url || '', link: p.link, featured: !!p.featured, order: p.order || 0 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.link.trim()) {
      toast.error('Name and link are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/marketplace/partners/${editingId}`, form);
        toast.success('Partner updated');
      } else {
        await api.post('/marketplace/partners', form);
        toast.success('Partner added');
      }
      setDialogOpen(false);
      fetchPartners();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save partner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete partner "${p.name}"?`)) return;
    try {
      await api.delete(`/marketplace/partners/${p.partner_id}`);
      toast.success('Partner deleted');
      setPartners((prev) => prev.filter((x) => x.partner_id !== p.partner_id));
    } catch (e) {
      toast.error('Failed to delete partner');
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="marketplace-page">
        <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
              <Store size={22} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Marketplace</h1>
              <p className="text-slate-500 text-sm mt-0.5">Trusted partners and tools, hand-picked for you.</p>
            </div>
          </div>
          {isMasterAdmin && (
            <Button onClick={openCreate} data-testid="marketplace-add-partner-btn" className="bg-rose-600 hover:bg-rose-700">
              <Plus size={16} className="mr-2" /> Add Partner
            </Button>
          )}
        </div>

        {loading ? (
          <div className="py-24 text-center text-slate-400" data-testid="marketplace-loading">Loading partners…</div>
        ) : partners.length === 0 ? (
          <div className="py-24 text-center border-2 border-dashed border-slate-200 rounded-2xl mt-6" data-testid="marketplace-empty">
            <Store size={40} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No partners yet.</p>
            {isMasterAdmin && <p className="text-slate-400 text-sm mt-1">Click “Add Partner” to feature your first partner.</p>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6" data-testid="marketplace-grid">
            {partners.map((p) => (
              <div
                key={p.partner_id}
                data-testid={`marketplace-card-${p.partner_id}`}
                className="relative flex flex-col bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                {p.featured && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" data-testid={`marketplace-featured-${p.partner_id}`}>
                    <Star size={11} className="fill-amber-500 text-amber-500" /> Featured
                  </span>
                )}
                <div className="h-16 flex items-center mb-4">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} className="max-h-16 max-w-[160px] object-contain" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400">
                      <ImageIcon size={22} />
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-slate-900 text-lg">{p.name}</h3>
                <p className="text-slate-500 text-sm mt-1 flex-1 line-clamp-4">{p.bio}</p>

                <div className="mt-4 flex items-center justify-between gap-2">
                  <Button
                    onClick={() => handleVisit(p)}
                    data-testid={`marketplace-visit-${p.partner_id}`}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    size="sm"
                  >
                    Visit partner <ExternalLink size={14} className="ml-1.5" />
                  </Button>
                  {isMasterAdmin && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-testid={`marketplace-clicks-${p.partner_id}`} title="Total clicks">
                        <MousePointerClick size={13} /> {p.click_count || 0}
                      </span>
                      <button onClick={() => openEdit(p)} data-testid={`marketplace-edit-${p.partner_id}`} className="text-slate-400 hover:text-indigo-600 p-1" title="Edit">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => handleDelete(p)} data-testid={`marketplace-delete-${p.partner_id}`} className="text-slate-400 hover:text-red-600 p-1" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add / Edit dialog (Master Admin only) */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="marketplace-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Partner' : 'Add Partner'}</DialogTitle>
              <DialogDescription>
                Partners appear in every tenant's Marketplace. Add a logo, a short bio and your affiliate link.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Partner name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Acme Analytics"
                  data-testid="marketplace-form-name"
                />
              </div>
              <div>
                <Label>Short bio</Label>
                <Textarea
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  placeholder="A short description of this partner…"
                  rows={3}
                  data-testid="marketplace-form-bio"
                />
              </div>
              <div>
                <Label>Logo / image</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.logo_url ? (
                    <img src={form.logo_url} alt="logo" className="h-12 w-12 object-contain border border-slate-200 rounded" />
                  ) : (
                    <div className="h-12 w-12 rounded border border-dashed border-slate-300 flex items-center justify-center text-slate-300">
                      <ImageIcon size={18} />
                    </div>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowMediaPicker(true)} data-testid="marketplace-form-pick-logo">
                    Choose from Media Library
                  </Button>
                  {form.logo_url && (
                    <button type="button" className="text-xs text-red-500" onClick={() => setForm({ ...form, logo_url: '' })} data-testid="marketplace-form-clear-logo">
                      Remove
                    </button>
                  )}
                </div>
                <Input
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="…or paste an image URL"
                  className="mt-2"
                  data-testid="marketplace-form-logo-url"
                />
              </div>
              <div>
                <Label>Affiliate link *</Label>
                <Input
                  value={form.link}
                  onChange={(e) => setForm({ ...form, link: e.target.value })}
                  placeholder="https://partner.com/?ref=yourcode"
                  data-testid="marketplace-form-link"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.featured}
                    onCheckedChange={(v) => setForm({ ...form, featured: v })}
                    data-testid="marketplace-form-featured"
                  />
                  <Label className="cursor-pointer">Featured (pin to top)</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-slate-500">Order</Label>
                  <Input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                    className="w-20"
                    data-testid="marketplace-form-order"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="marketplace-form-save">
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add partner'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <MediaPickerModal
          open={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(media) => { setForm((f) => ({ ...f, logo_url: media.url })); setShowMediaPicker(false); }}
        />
      </div>
    </Layout>
  );
};

export default Marketplace;
